import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { endOfDay, startOfDay } from 'date-fns';

import { BottomSheet } from '@/components/common/BottomSheet';
import { Popup, SingleTargetSelector } from '@/components';
import { useIsPhoneOrHighScreen } from '@/shared/hooks/useIsPhoneOrHighScreen';
import { type DetailedRoute } from '@/generated/model/detailedRoute';
import { RoutesHeader } from '@/components/routes/RoutesHeader';
import { RoutesDateSelect } from '@/components/routes/RoutesDateSelect';
import { RoutesList } from '@/components/routes/RoutesList';
import { RoutesResultHeader } from '@/components/routes/RoutesResultHeader';
import { useRoutesData } from '@/components/routes/useRoutesData';

import { ctxPluto } from '@/contexts/PlutoContext';
import { ctxRoutes } from '@/contexts/RoutesCtx.tsx';
import { type MinimalTarget } from '@/generated/model';
import { rethrowIfNotClientException } from '@/utilities/throwableUtils';
import { TabValue } from '@/types';
import { shouldShowTargetSelector } from '@/components/routes/utils/routesPopupUtils';
import { useTargetSelector } from '@/contexts/useTargetSelector';

function Body() {
  const routesCtx = useContext(ctxRoutes);
  const plutoCtx = useContext(ctxPluto);
  const { closeTargetSelector, openTargetSelector } = useTargetSelector();

  const setSelectedTarget = routesCtx?.setSelectedTarget;
  const setTab = routesCtx?.setTab;
  const setSelectedDates = routesCtx?.setSelectedDates;
  const setRoutes = routesCtx?.setRoutes;
  const setIsBottomSheetOpen = routesCtx?.setIsBottomSheetOpen;
  const setAdjustMap = routesCtx?.setAdjustMap;
  const setSelectedRoutes = plutoCtx?.setSelectedRoutes;

  useEffect(() => {
    if (routesCtx?.selectedTarget) {
      setTab?.(TabValue.dateSelect);
    }
  }, [routesCtx?.selectedTarget, setTab]);

  const setSelectedTargetWrapper = useCallback(
    (target: MinimalTarget | undefined) => {
      if (!target || !setSelectedTarget || !setTab || !setSelectedDates || !setRoutes) {
        return;
      }
      setSelectedTarget(target);
      setTab(TabValue.dateSelect);
      setSelectedDates(undefined);
      setRoutes(undefined);
      setSelectedRoutes?.([]);
    },
    [setRoutes, setSelectedDates, setSelectedRoutes, setSelectedTarget, setTab]
  );

  const setRoutesWrapper = useCallback(
    (values: DetailedRoute[] | undefined) => {
      if (!setRoutes) {
        return;
      }
      setRoutes(values);
      setSelectedRoutes?.([]);
      setIsBottomSheetOpen?.(true);
      setAdjustMap?.(true);
      closeTargetSelector();
    },
    [closeTargetSelector, setAdjustMap, setIsBottomSheetOpen, setRoutes, setSelectedRoutes]
  );

  const setSelectedDatesWrapper = useCallback(
    (from: Date, to: Date) => {
      if (!setSelectedDates) {
        return;
      }
      setSelectedDates({ from, to });
    },
    [setSelectedDates]
  );

  if (!routesCtx) return null;

  switch (routesCtx.tab) {
    case TabValue.targetSelect:
      return (
        <SingleTargetSelector
          setSelectedTarget={setSelectedTargetWrapper}
          selectedTarget={routesCtx.selectedTarget}
          searchText={routesCtx.searchText}
          setSearchText={routesCtx.setSearchText}
        />
      );
    case TabValue.dateSelect:
      return (
        <RoutesDateSelect
          selectedTarget={routesCtx.selectedTarget}
          setRoutes={setRoutesWrapper}
          setSelectedDates={setSelectedDatesWrapper}
          openTargetSelector={openTargetSelector}
        />
      );
    default:
      // ez lehet nem kell ?? nem kell h undefined lehessen ?
      return (
        <SingleTargetSelector
          setSelectedTarget={setSelectedTargetWrapper}
          selectedTarget={routesCtx.selectedTarget}
          searchText={routesCtx.searchText}
          setSearchText={routesCtx.setSearchText}
        />
      );
  }
}

function RoutesSecondary() {
  const routesCtx = useContext(ctxRoutes);
  const plutoCtx = useContext(ctxPluto);

  const today = new Date();
  const from = startOfDay(today);
  const to = endOfDay(today);

  useEffect(() => {
    routesCtx?.setRoutes([]);
  }, [routesCtx?.selectedTarget]);

  if (!routesCtx || !plutoCtx) return;

  async function setSelectedTargetWrapper(target: MinimalTarget | undefined) {
    if (!routesCtx || !plutoCtx || !target) {
      return;
    }

    routesCtx.setSelectedTarget(target);
    if (!routesCtx.selectedDates) routesCtx.setSelectedDates({ from, to });
  }

  if (!routesCtx) return null;
  return (
    <div className={`route-panel-target-selector ${routesCtx.routesLoading ? 'disabled' : ''}`}>
      <SingleTargetSelector
        setSelectedTarget={setSelectedTargetWrapper}
        selectedTarget={routesCtx.selectedTarget}
        searchText={routesCtx.searchText}
        setSearchText={routesCtx.setSearchText}
      />
    </div>
  );
}

export default function RoutesPopup() {
  const plutoCtx = useContext(ctxPluto);
  const routesCtx = useContext(ctxRoutes);
  const { closeTargetSelector, isTargetSelectorOpen } = useTargetSelector();
  const { t } = useTranslation();
  const [dismissedTargetSelectorTargetId, setDismissedTargetSelectorTargetId] = useState<number>();
  const summaryRef = useRef<HTMLDivElement | null>(null);
  const selectedDatesRef = useRef<{ from: Date; to: Date }>(routesCtx?.selectedDates);
  const selectedTargetRef = useRef<MinimalTarget>(routesCtx?.selectedTarget);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const target: MinimalTarget | undefined = location.state?.target;
  const setSelectedTarget = routesCtx?.setSelectedTarget;
  const setSelectedDates = routesCtx?.setSelectedDates;
  const setPositions = routesCtx?.setPositions;
  const setActualToTime = routesCtx?.setActialToTime;
  const setBottomSheetOpen = routesCtx?.setIsBottomSheetOpen;
  const setRoutes = routesCtx?.setRoutes;
  const setHighlightedRoutes = routesCtx?.setHighlightedRoutes;
  const setSelectedRoutes = plutoCtx?.setSelectedRoutes;
  const selectedTargetId = routesCtx?.selectedTarget?.id;
  const routeTargetId = target?.id ?? selectedTargetId;
  const [quickRouteDates] = useState(() => {
    const today = new Date();
    return { from: startOfDay(today), to: endOfDay(today) };
  });
  const isPhone = useIsPhoneOrHighScreen();
  const query = useRoutesData();
  const showTargetSelector = shouldShowTargetSelector({
    isTargetSelectorOpen,
    targetId: routeTargetId,
    selectedTargetId,
    dismissedTargetId: dismissedTargetSelectorTargetId,
    hasEmptyQueryResult: Boolean(query.isSuccess && !query.isFetching && query.data?.routes.length === 0),
  });

  useEffect(() => {
    const isOpen = searchParams.get('bottomsheet');
    routesCtx?.setIsBottomSheetOpen(isOpen === 'true' && routesCtx.selectedTarget !== undefined);
    return () => routesCtx?.setIsBottomSheetOpen(false);
  }, []);

  useEffect(() => {
    if (!target) {
      return;
    }
    setRoutes?.(undefined);
    setPositions?.(undefined);
    setActualToTime?.(undefined);
    setHighlightedRoutes?.([]);
    setBottomSheetOpen?.(false);
    setSelectedDates?.(quickRouteDates);
    setSelectedTarget?.(target);
    setSelectedRoutes?.([]);
  }, [
    quickRouteDates,
    setActualToTime,
    setHighlightedRoutes,
    setPositions,
    setRoutes,
    setSelectedDates,
    setSelectedRoutes,
    setSelectedTarget,
    target,
  ]);

  useEffect(() => {
    if (query.error) {
      rethrowIfNotClientException(query.error);
    }
  }, [query.error]);

  useEffect(() => {
    if (
      !isPhone ||
      showTargetSelector ||
      !routeTargetId ||
      selectedTargetId !== routeTargetId ||
      !routesCtx?.routes?.length
    ) {
      return;
    }
    // The route data hook writes the context from a parent effect, so open the sheet after routes arrive.
    setBottomSheetOpen?.(true);
  }, [isPhone, routeTargetId, routesCtx?.routes, selectedTargetId, setBottomSheetOpen, showTargetSelector]);

  useEffect(() => {
    plutoCtx?.setMode('route'); // show selectedTargets on the map
  }, [plutoCtx]);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set('bottomsheet', routesCtx?.isBottomSheetOpen ? 'true' : 'false');
    setSearchParams(params, {
      preventScrollReset: true,
    });

    if (routesCtx?.isBottomSheetOpen) {
      closeTargetSelector();
    }
  }, [closeTargetSelector, routesCtx?.isBottomSheetOpen, setSearchParams]);

  useEffect(() => {
    if (
      !selectedDatesRef ||
      routesCtx?.selectedDates !== selectedDatesRef.current ||
      !selectedTargetRef ||
      routesCtx?.selectedTarget !== selectedTargetRef.current
    ) {
      routesCtx?.setHighlightedRoutes([]);
      selectedDatesRef.current = routesCtx?.selectedDates;
      selectedTargetRef.current = routesCtx?.selectedTarget;
    }
  }, [routesCtx?.routes]);

  if (!routesCtx) return;

  const bottomSheetKey = [
    routesCtx.selectedTarget?.id ?? '',
    routesCtx.selectedDates?.from.getTime() ?? '',
    routesCtx.selectedDates?.to.getTime() ?? '',
  ].join(':');

  return isPhone ? (
    <>
      {showTargetSelector && (
        <Popup
          popupTitle={t('mainMenu.routes')}
          header={<RoutesHeader />}
          body={<Body />}
          footer={undefined}
          isFullScr={true}
          leftText={true}
          closePopup={async () => {
            closeTargetSelector();
            setDismissedTargetSelectorTargetId(routeTargetId);
            if (routesCtx.routes && routesCtx.routes.length !== 0) {
              routesCtx.setIsBottomSheetOpen(true);
              routesCtx.setAdjustMap(true);
            }
          }}
        />
      )}
      {routesCtx.isBottomSheetOpen && !showTargetSelector && (
        <BottomSheet
          key={bottomSheetKey}
          header={<RoutesResultHeader />}
          bottomSheetUp={routesCtx.isBottomSheetUp}
          setBottomSheetUp={routesCtx.setIsBottomSheetUp}
          routeTab={true}
        >
          <RoutesList topRef={summaryRef} />
        </BottomSheet>
      )}
    </>
  ) : (
    <>
      <RoutesSecondary />
      {routesCtx.routes && <RoutesList topRef={summaryRef} />}
    </>
  );
}
